import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Camera, Image as ImageIcon, Sparkles, TrendingUp, 
  ChevronRight, Calendar, Info, Shield, Share2, 
  Trash2, Plus, Zap, Heart, Trophy, Clock, Lock
} from 'lucide-react';
import { User, ProgressPhoto, AIAnalysis } from './types';
import { fetchUserPhotos, saveProgressPhoto } from './services/progressService';
import { BeforeAfterSlider } from './components/BeforeAfterSlider';

export const ProgressDashboard = ({ user }: { user: User }) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<ProgressPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<ProgressPhoto | null>(null);

  useEffect(() => {
    loadPhotos();
  }, [user.id]);

  const loadPhotos = async () => {
    setLoading(true);
    const data = await fetchUserPhotos(user.id);
    setPhotos(data);
    setLoading(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target?.result as string;
      try {
        await saveProgressPhoto(user, base64, { privacy: 'private' });
        await loadPhotos();
      } catch (err) {
        console.error(err);
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const stats = {
    total: photos.length,
    lastUpdate: photos[0]?.uploadedAt?.toDate?.() || new Date(),
    weightDiff: photos.length > 1 ? (photos[0].weight || 0) - (photos[photos.length - 1].weight || 0) : 0
  };

  return (
    <div className="p-4 md:p-8 pb-32 space-y-8 max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-black italic tracking-tighter flex items-center gap-3">
            <Camera className="text-emerald-400" size={40} /> {t('progress.title')}
          </h1>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-widest">{t('progress.subtitle')}</p>
        </div>
        <div className="flex gap-3">
           <label className="flex items-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-[2rem] font-black shadow-lg shadow-emerald-500/20 transition-all cursor-pointer">
             <Plus size={20} /> {t('feed.new_post')}
             <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} />
           </label>
        </div>
      </div>

      {isUploading && (
        <div className="bg-slate-900 border border-emerald-500/30 p-8 rounded-[2.5rem] flex flex-col items-center justify-center animate-pulse">
           <Zap className="text-emerald-500 animate-bounce mb-4" size={40} />
           <p className="font-black text-emerald-400 uppercase tracking-widest">{t('coach.thinking')}</p>
        </div>
      )}

      {/* Hero Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] space-y-4">
           <div className="flex justify-between items-center">
             <div className="p-3 bg-emerald-500/10 text-emerald-500 rounded-2xl"><ImageIcon size={24}/></div>
             <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('progress.total_photos')}</span>
           </div>
           <div className="text-4xl font-black text-white">{stats.total}</div>
           <p className="text-xs text-slate-500 font-bold uppercase italic tracking-tighter">{t('progress.timeline')}: {photos.length} {t('progress.photos')}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] space-y-4">
           <div className="flex justify-between items-center">
             <div className="p-3 bg-orange-500/10 text-orange-500 rounded-2xl"><TrendingUp size={24}/></div>
             <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('progress.weight_diff')}</span>
           </div>
           <div className={`text-4xl font-black ${stats.weightDiff <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
             {stats.weightDiff > 0 ? '+' : ''}{stats.weightDiff.toFixed(1)} <span className="text-xl">KG</span>
           </div>
        </div>
        <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] space-y-4">
           <div className="flex justify-between items-center">
             <div className="p-3 bg-cyan-500/10 text-cyan-400 rounded-2xl"><Zap size={24} fill="currentColor"/></div>
             <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('progress.ai_status')}</span>
           </div>
           <div className="text-4xl font-black text-white uppercase italic tracking-tighter">{t('progress.ready')}</div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Timeline Gallery */}
        <div className="lg:col-span-2 space-y-8">
          <div className="flex justify-between items-center px-4">
            <h2 className="text-xl font-black flex items-center gap-3 uppercase italic tracking-tighter"><Clock size={20} className="text-emerald-400"/> {t('progress.foto_timeline')}</h2>
            <button 
              onClick={() => setShowComparison(!showComparison)}
              className={`text-xs font-black uppercase tracking-widest py-2 px-6 rounded-xl transition-all ${showComparison ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
            >
              {t('progress.compare')}
            </button>
          </div>

          {showComparison && photos.length >= 2 ? (
            <div className="animate-scaleIn">
              <BeforeAfterSlider 
                before={{ 
                  url: photos[photos.length - 1].imageUrl, 
                  label: t('progress.before'), 
                  stats: `${photos[photos.length - 1].weight}kg` 
                }}
                after={{ 
                  url: photos[0].imageUrl, 
                  label: t('progress.after'), 
                  stats: `${photos[0].weight}kg` 
                }}
              />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {photos.map((photo, i) => (
                <div 
                  key={photo.id} 
                  onClick={() => setSelectedPhoto(photo)}
                  className="bg-slate-900 border border-slate-800 rounded-[2rem] overflow-hidden group cursor-pointer hover:border-emerald-500/30 transition-all shadow-xl"
                >
                  <div className="aspect-[3/4] relative overflow-hidden">
                    <img src={photo.imageUrl} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="Progress" />
                    {photo.aiAnalysis && (
                      <div className="absolute top-4 right-4 bg-emerald-500 text-white text-[8px] font-black px-2 py-1 rounded-md shadow-lg flex items-center gap-1">
                        <Sparkles size={10}/> AI
                      </div>
                    )}
                  </div>
                  <div className="p-4 border-t border-slate-800 flex justify-between items-center">
                    <div>
                      <p className="text-xs font-black text-white">{photo.weight} KG</p>
                      <p className="text-[10px] text-slate-500 font-bold uppercase">
                        {new Date(photo.uploadedAt?.toDate?.() || Date.now()).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar Lab / AI Analysis */}
        <div className="space-y-8">
          <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-6">
            <h2 className="text-xl font-black flex items-center gap-3 uppercase italic tracking-tighter"><Sparkles size={20} className="text-emerald-400"/> {t('progress.ai_insights')}</h2>
            {selectedPhoto?.aiAnalysis ? (
              <div className="space-y-6 animate-fadeIn">
                 <div className="bg-emerald-500/5 border border-emerald-500/20 p-6 rounded-3xl space-y-4">
                    <div className="flex justify-between items-center">
                       <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('progress.body_fat_est')}</span>
                       <span className="text-xl font-black text-emerald-400">{selectedPhoto.aiAnalysis.bodyFatPercentage}</span>
                    </div>
                 </div>
                 <div className="p-6 bg-slate-800/30 rounded-3xl border border-slate-700/50 space-y-3">
                    <p className="text-sm font-medium italic text-slate-300">"{selectedPhoto.aiAnalysis.encouragement}"</p>
                 </div>
              </div>
            ) : (
              <div className="py-20 text-center space-y-3">
                 <Sparkles size={32} className="mx-auto text-slate-600" />
                 <p className="text-sm text-slate-500 font-bold">
                   {t('progress.ai_insights_placeholder') || 'AI insights will appear here after you upload progress photos'}
                 </p>
                 <p className="text-xs text-slate-600">
                   {t('progress.ai_insights_placeholder_subtitle') || 'Upload a photo to get started'}
                 </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};